// Structured error context for Signal WASM calls.
//
// The WASM crate raises bare `Error: Operation failed` with no detail about
// which step failed or under what conditions, which makes every E2EE incident
// a 30-message debugging session. `E2eeError` wraps a WASM failure with:
//   - `step`   : the WASM entry point that threw
//   - `context`: the relevant identifiers at the call site (peer, device,
//                ciphertext type, prekey ids, etc.)
//   - `cause`  : the original error
//
// Use `wrapWasm(step, context, () => client.foo(...))` at the call site. The
// rendered message — `E2eeError[step]: Operation failed (k1=v1 k2=v2)` —
// lets the next incident be diagnosed from one log line.

export type E2eeStep =
  | 'decrypt_message'
  | 'decrypt_group_message'
  | 'encrypt_message'
  | 'encrypt_group_message'
  | 'process_pre_key_bundle'
  | 'process_sender_key_distribution'
  | 'create_sender_key_distribution_message';

export interface E2eeErrorContext {
  [key: string]: string | number | boolean | null | undefined;
}

export class E2eeError extends Error {
  readonly step: E2eeStep;
  readonly context: E2eeErrorContext;
  // The bare WASM error message (e.g. 'Operation failed'). Preserved separately
  // so callers can match on it without parsing the wrapped string.
  readonly causeMessage: string;
  // Standard Error.cause is also set so console / DevTools chain the original.
  override readonly cause: unknown;

  constructor(step: E2eeStep, context: E2eeErrorContext, cause: unknown) {
    const causeMessage = cause instanceof Error ? cause.message : String(cause);
    const ctxString = Object.entries(context)
      .filter(([, v]) => v !== undefined)
      .map(([k, v]) => `${k}=${v}`)
      .join(' ');
    super(
      `E2eeError[${step}]: ${causeMessage}${ctxString ? ` (${ctxString})` : ''}`,
    );
    this.name = 'E2eeError';
    this.step = step;
    this.context = context;
    this.causeMessage = causeMessage;
    this.cause = cause;
  }
}

export async function wrapWasm<T>(
  step: E2eeStep,
  context: E2eeErrorContext,
  fn: () => Promise<T>,
): Promise<T> {
  try {
    return await fn();
  } catch (cause) {
    // If we already wrapped this (e.g. nested call sites), surface the
    // innermost wrapped error — re-wrapping would obscure the original step.
    if (cause instanceof E2eeError) throw cause;
    throw new E2eeError(step, context, cause);
  }
}

// ── P4: actionable failure taxonomy ─────────────────────────────────────────
//
// `step` says WHICH WASM call threw; on its own it does not tell a user (or the
// UI) what to DO. `classifyE2eeError` maps a raw failure into one of a small set
// of actionable categories so the UI can offer a real remedy — most importantly
// a "reset secure session" affordance — instead of a dead "couldn't decrypt"
// bubble. Pure function (no WASM, no IO): safe to call from the render path.

export type E2eeFailureCategory =
  // Session establishment failed (`process_pre_key_bundle` / send-side
  // `encrypt_message`) — a stale/poisoned peer bundle or PQ (Kyber) mismatch.
  | 'establish'
  // Inbound PreKeyMessage (type-3) decrypt failed — new-session prekey desync.
  | 'prekey_desync'
  // Inbound WhisperMessage (type-2) decrypt failed — the double ratchet diverged.
  | 'ratchet_divergence'
  // Group sender-key path failed — recovery is by re-sharing keys, not a 1:1 reset.
  | 'group_session'
  // A peer's identity key changed (H-3). Requires EXPLICIT user acknowledgment
  // after out-of-band verification — never an automatic retry/reset.
  | 'identity_key_changed'
  // A group ciphertext's distribution id does not match the group's current
  // distribution id (M-2) — potential replay/tamper. Not user-resettable.
  | 'group_distid_mismatch'
  // Not an E2eeError, or a step with no specific remedy.
  | 'unknown';

export interface E2eeTriage {
  category: E2eeFailureCategory;
  // Whether a user-initiated "reset secure session" (fresh 1:1 re-establishment)
  // is a sensible remedy for this failure.
  resettable: boolean;
  // Short, jargon-free line for the error affordance.
  title: string;
  // One-line technical hint for the details expander / logs.
  hint: string;
}

const TRIAGE: Record<
  E2eeFailureCategory,
  { title: string; hint: string; resettable: boolean }
> = {
  establish: {
    title: 'Secure session could not be set up',
    hint: 'Session establishment failed — the peer key bundle may be stale or the post-quantum (Kyber) key mismatched. Resetting the secure session re-establishes it.',
    resettable: true,
  },
  prekey_desync: {
    title: "This message couldn't be decrypted",
    hint: 'New-session prekey desync (type-3). A fresh secure session can recover future messages.',
    resettable: true,
  },
  ratchet_divergence: {
    title: "This message couldn't be decrypted",
    hint: 'The secure session has diverged (type-2 ratchet mismatch). Resetting the secure session re-syncs it.',
    resettable: true,
  },
  group_session: {
    title: "This group message couldn't be decrypted",
    hint: 'Group session-key issue — recovery is by the sender re-sharing keys, not a 1:1 session reset.',
    resettable: false,
  },
  identity_key_changed: {
    title: "This contact's security identity changed",
    hint: "The contact's identity key no longer matches the one previously seen. This can be legitimate (reinstall, new device) or a man-in-the-middle attack. Do NOT retry automatically — verify the safety number with the contact out-of-band and explicitly acknowledge the change.",
    resettable: false,
  },
  group_distid_mismatch: {
    title: 'Group message failed a safety check',
    hint: "The ciphertext's distribution id does not match this group's current distribution id — the message was NOT decrypted. This can indicate replay/tampering or a stale server record; it is not fixable by resetting a session.",
    resettable: false,
  },
  unknown: {
    title: "This message couldn't be decrypted",
    hint: 'Unrecognized decryption failure.',
    resettable: false,
  },
};

export function classifyE2eeError(err: unknown): E2eeTriage {
  let category: E2eeFailureCategory = 'unknown';
  // Explicit failure codes take precedence over step-based classification.
  // Callers throw either a plain Error with a `code` property (the codebase
  // convention, e.g. 'e2ee_recipient_no_devices') or an E2eeError carrying
  // the code in its context.
  const code =
    err instanceof E2eeError
      ? err.context.code
      : (err as { code?: unknown } | null | undefined)?.code;
  if (code === 'identity_key_changed' || code === 'group_distid_mismatch') {
    category = code;
  } else if (err instanceof E2eeError) {
    switch (err.step) {
      case 'process_pre_key_bundle':
      case 'encrypt_message':
        category = 'establish';
        break;
      case 'decrypt_message':
        // ciphertext type 3 = PreKeyMessage (new session); anything else is a
        // WhisperMessage (established session) whose ratchet diverged. Coerce:
        // a server-supplied string '3' must classify the same as numeric 3.
        category =
          Number(err.context?.type) === 3
            ? 'prekey_desync'
            : 'ratchet_divergence';
        break;
      case 'decrypt_group_message':
      case 'process_sender_key_distribution':
      case 'encrypt_group_message':
      case 'create_sender_key_distribution_message':
        category = 'group_session';
        break;
    }
  }
  return { category, ...TRIAGE[category] };
}
